/**
 * BlockDatesModal Component
 *
 * Modal for blocking/unblocking dates.
 */

import React, { useState } from 'react';

interface BlockDatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDates: Date[];
  hasBlockedDates: boolean;
  onBlock: (reason?: string) => Promise<void>;
  onUnblock: () => Promise<void>;
  isLoading: boolean;
}

const formatDateRange = (dates: Date[]): string => {
  if (dates.length === 0) return '';
  if (dates.length === 1) {
    return dates[0].toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
  const first = sortedDates[0];
  const last = sortedDates[sortedDates.length - 1];

  return `${first.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })} - ${last.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

export const BlockDatesModal: React.FC<BlockDatesModalProps> = ({
  isOpen,
  onClose,
  selectedDates,
  hasBlockedDates,
  onBlock,
  onUnblock,
  isLoading,
}) => {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleBlock = async () => {
    await onBlock(reason || undefined);
    setReason('');
    onClose();
  };

  const handleUnblock = async () => {
    await onUnblock();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {hasBlockedDates ? 'Manage Blocked Dates' : 'Block Dates'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">Selected dates:</p>
          <p className="font-medium text-gray-900">
            {formatDateRange(selectedDates)}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {selectedDates.length} {selectedDates.length === 1 ? 'night' : 'nights'}
          </p>
        </div>

        {!hasBlockedDates && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (optional)
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select a reason</option>
              <option value="Personal use">Personal use</option>
              <option value="Maintenance">Maintenance</option>
              <option value="Renovation">Renovation</option>
              <option value="Other booking">Other booking (external)</option>
              <option value="Other">Other</option>
            </select>
          </div>
        )}

        <div className="flex gap-3">
          {hasBlockedDates ? (
            <>
              <button
                onClick={handleUnblock}
                disabled={isLoading}
                className="flex-1 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 disabled:opacity-50"
              >
                {isLoading ? 'Unblocking...' : 'Unblock dates'}
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBlock}
                disabled={isLoading}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 disabled:opacity-50"
              >
                {isLoading ? 'Blocking...' : 'Block dates'}
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-4 text-center">
          {hasBlockedDates
            ? 'Unblocking will make these dates available for booking.'
            : 'Blocked dates cannot be booked by guests.'}
        </p>
      </div>
    </div>
  );
};

// Icon
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default BlockDatesModal;
